import { useState } from "react";
import { ICaring } from "../../Models/ICaring";
import { ISentinelRich } from "../../Models/ISentinel";
import { IUser } from "../../Models/IUser";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { devLabel, dispName, emailsOf, hasPerson, mailReason, operatorsOf } from "./SetupHelpers";
import { Btn, CheckBox, Chips, Dialog, Pill, SearchBox } from "./SetupUi";

// «Operatori assegnati a ...» (risposta 3): una casella per operatore. Assegnare decide cosa vede
// l'operatore (allarmi e video sulla Home); l'email è una conseguenza. Stesse chiamate di Carings:
// un abbinamento in più o in meno per ogni casella cambiata.

const effect = (u: IUser) => {
  const r = mailReason(u);
  if (!r) return "riceve anche l'email: " + emailsOf(u).join(", ");
  if (r === "nessuna email registrata") return "solo sulla Home: senza email";
  if (r === "notifiche email spente") return "solo sulla Home: notifiche email spente";
  return "utente disabilitato: non entra e non vede niente";
};

const OperatorsDialog = (props: {
  sentinel: ISentinelRich;
  users: IUser[];
  carings: ICaring[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) => {
  const s = props.sentinel;
  const [orig] = useState<number[]>(() => operatorsOf(s, props.carings, props.users).map((u) => u.id));
  const [sel, setSel] = useState<number[]>(orig);
  const [q, setQ] = useState<string>("");
  const [onlySel, setOnlySel] = useState<string>("tutti");
  const [saving, setSaving] = useState<boolean>(false);

  const byId = (id: number) => props.users.find((u) => u.id === id);
  const add = sel.filter((x) => !orig.includes(x));
  const del = orig.filter((x) => !sel.includes(x));
  const many = props.users.length > 8;
  const list = props.users.filter(
    (u) => (onlySel !== "sel" || sel.includes(u.id)) && (!q.trim() || u.identifier.toLowerCase().includes(q.trim().toLowerCase()))
  );
  const n = sel.length;
  const withMail = sel.map(byId).filter((u) => u && !mailReason(u)).length;
  const summary = `${n} ${n === 1 ? "operatore assegnato" : "operatori assegnati"} · ${withMail} ${withMail === 1 ? "riceve" : "ricevono"} anche l'email`;
  const changes = [
    add.length ? "aggiunge " + add.map((x) => byId(x)?.identifier).join(", ") : "",
    del.length ? "toglie " + del.map((x) => byId(x)?.identifier).join(", ") : "",
  ]
    .filter(Boolean)
    .join(", ");
  // dispositivi vecchi senza persona collegata: l'assegnazione passa dalla persona, qui non si può
  const why = saving
    ? "Salvataggio in corso..."
    : !s.patient
    ? "Questo dispositivo non ha una persona collegata: si imposta da Configurazione."
    : add.length || del.length
    ? null
    : "Nessuna modifica da salvare.";
  const label = add.length || del.length ? `Salva (${[add.length ? "+" + add.length : "", del.length ? "-" + del.length : ""].filter(Boolean).join(" ")})` : "Salva";

  const save = async () => {
    if (why) return;
    setSaving(true);
    const failed: string[] = [];
    try {
      await saveAll(failed);
    } catch (e) {
      failed.push("errore di rete");
    }
    if (failed.length) {
      props.onSaved("Operatori di " + dispName(s) + ": non salvati per " + failed.join(", ") + ". Riprova.");
      return;
    }
    props.onSaved("Operatori assegnati a " + dispName(s) + ": salvato.");
  };

  const saveAll = async (failed: string[]) => {
    for (const u of add) {
      const r: IAPIResponse = await ApiService.CaringController.CreateCaring(s.patient.id, u);
      if (r.error !== null) failed.push(byId(u)?.identifier ?? String(u));
    }
    for (const u of del) {
      const r: IAPIResponse = await ApiService.CaringController.DeleteCaring(s.patient.id, u);
      if (r.error !== null) failed.push(byId(u)?.identifier ?? String(u));
    }
  };

  const title = "Operatori assegnati a " + dispName(s);
  return (
    <Dialog
      title={
        <>
          {title}
          {hasPerson(s.patient) && <span className="sub" style={{ fontSize: 14, fontWeight: 400 }}> {devLabel(s)}</span>}
        </>
      }
      ariaTitle={title}
      why={summary + (why && !saving ? " · nessuna modifica" : changes ? " · " + changes : "")}
      onClose={() => {
        if (!saving) props.onClose();
      }}
      footer={
        <>
          <Btn text="Annulla" why={saving ? "Salvataggio in corso" : null} onClick={props.onClose} />
          <Btn text={saving ? "Salvataggio..." : label} primary spin={saving} why={why} onClick={save} />
        </>
      }
    >
      <div style={{ lineHeight: "20px" }}>
        Gli operatori spuntati seguono <b>{dispName(s)}</b>: ne vedono allarmi e video sulla Home. Chi ha l'email e le notifiche accese riceve anche
        l'email degli allarmi.
      </div>
      {many && (
        <div className="tb" style={{ margin: 0 }}>
          <SearchBox value={q} placeholder="Cerca operatore" onChange={setQ} />
          <Chips
            chips={[
              { key: "tutti", text: "Tutti" },
              { key: "sel", text: "Selezionati" },
            ]}
            selected={onlySel}
            onChange={setOnlySel}
          />
        </div>
      )}
      <div className="olist">
        {list.length === 0 && <div className="orow">Nessun operatore</div>}
        {list.map((u) => {
          const on = sel.includes(u.id);
          return (
            <button
              type="button"
              key={u.id}
              className="orow"
              aria-pressed={on}
              onClick={() => setSel(on ? sel.filter((x) => x !== u.id) : [...sel, u.id])}
            >
              <CheckBox on={on} />
              <span className="grow">
                <span className="el" style={{ fontWeight: 600 }}>
                  {u.identifier}
                </span>
                <span className="sub el">{effect(u)}</span>
              </span>
              {!u.enabled && <Pill kind="warn" text="disabilitato" />}
            </button>
          );
        })}
      </div>
    </Dialog>
  );
};

export default OperatorsDialog;
